import { useEffect, useState } from 'react';

// api
import { api } from '@/api/axios';

// project
import { loadAmoChat, destroyAmoChat } from 'components/amoChat/amoChatLoader';

async function getAmoChatConfig() {
  try {
    const res = await api.post('/gates/data/get_amo_chat_config', {});
    return res.data;
  } catch (error) {
    console.error('amoChat: не удалось получить настройки чата', error);
    return { SUCCESS: false, ERROR: { CODE: 'REQUEST_FAILED' } };
  }
}

// Сколько ждать onChatReady, прежде чем показать ошибку
const READY_TIMEOUT_MS = 15000;

function buildOnlineChat(config) {
  const profile = {};
  if (config.NAME) profile.name = config.NAME;
  if (config.PHONE) profile.phone = config.PHONE;

  return {
    user_id: config.USER_ID,
    // profile — отображаемые данные в чате, личность задаёт только user_id
    profile
  };
}

// Данные для setMeta: попадают в карточку обращения в amoCRM.
// Проверено 17.09.2026: виджет доносит только имя контакта и название сделки. `note` и
// `custom_fields_values` он игнорирует — в button.js слова `note` нет вовсе, а поля контакта
// заполняются из onlinechat.profile. Поэтому ни примечание с токеном, ни раздел ЛК, ни телефон
// отдельным полем передать нельзя: телефон попадает в карточку из profile.phone.
function buildMeta(config) {
  const meta = {};
  // Имя применяется только при создании контакта и сделки: переименовать их потом можно лишь
  // через API amoCRM, с бэкенда. Поэтому чат не показываем пациентам без заполненного профиля.
  if (config.NAME) {
    meta.contact = { name: config.NAME };
    meta.lead = { name: config.NAME };
  }
  return meta;
}

/**
 * Подключает онлайн-чат amoCRM (плавающая кнопка) на время жизни компонента.
 * Возвращает статус: 'loading' | 'ready' | 'error'.
 */
export default function useAmoChat({ enabled = true } = {}) {
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (!enabled) return undefined;

    let active = true;
    let owner = null;
    let timer = null;

    (async () => {
      try {
        const data = await getAmoChatConfig();
        if (!active) return;

        // без user_id чат не загружаем: иначе переписка привяжется к браузеру, а не к пациенту
        if (data?.SUCCESS !== true || !data?.DATA?.USER_ID) {
          setStatus('error');
          return;
        }

        owner = loadAmoChat({
          onlinechat: buildOnlineChat(data.DATA),
          meta: buildMeta(data.DATA),
          onReady: () => active && setStatus('ready'),
          onError: () => active && setStatus('error')
        });

        timer = setTimeout(() => {
          if (active) setStatus((prev) => (prev === 'loading' ? 'error' : prev));
        }, READY_TIMEOUT_MS);
      } catch (error) {
        console.error(error);
        if (active) setStatus('error');
      }
    })();

    return () => {
      active = false;
      clearTimeout(timer);
      if (owner) destroyAmoChat(owner);
      setStatus('loading');
    };
  }, [enabled]);

  return status;
}
